import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastItem {
    id: string;
    tone: ToastTone;
    title?: string;
    message: string;
    durationMs: number | null;
    actionLabel?: string;
    onAction?: () => void;
}

interface CreateToastInput {
    tone: ToastTone;
    title?: string;
    message: string;
    durationMs?: number | null;
    actionLabel?: string;
    onAction?: () => void;
}

interface ToastState {
    items: ToastItem[];
    push: (input: CreateToastInput) => string;
    dismiss: (id: string) => void;
    clear: () => void;
}

const MAX_TOASTS = 3;
const DEFAULT_DURATION_MS = 3000;
const activeTimers = new Map<string, number>();

function nextToastId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }

    return `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function clearToastTimer(id: string): void {
    const timerId = activeTimers.get(id);

    if (timerId !== undefined) {
        window.clearTimeout(timerId);
        activeTimers.delete(id);
    }
}

export const useToastStore = create<ToastState>((set) => ({
    items: [],
    push: (input) => {
        const id = nextToastId();
        const toast: ToastItem = {
            id,
            tone: input.tone,
            title: input.title,
            message: input.message,
            durationMs: input.durationMs === undefined ? DEFAULT_DURATION_MS : input.durationMs,
            actionLabel: input.actionLabel,
            onAction: input.onAction,
        };

        set((state) => {
            const nextItems = [...state.items, toast];
            const overflow = nextItems.length - MAX_TOASTS;

            if (overflow <= 0) {
                return { items: nextItems };
            }

            const removed = nextItems.slice(0, overflow);
            removed.forEach((item) => clearToastTimer(item.id));

            return { items: nextItems.slice(overflow) };
        });

        if (toast.durationMs !== null) {
            const timerId = window.setTimeout(() => {
                useToastStore.getState().dismiss(id);
            }, toast.durationMs);

            activeTimers.set(id, timerId);
        }

        return id;
    },
    dismiss: (id) => {
        clearToastTimer(id);
        set((state) => ({
            items: state.items.filter((item) => item.id !== id),
        }));
    },
    clear: () => {
        Array.from(activeTimers.keys()).forEach((id) => clearToastTimer(id));
        set({ items: [] });
    },
}));

export function useToast() {
    const push = useToastStore((state) => state.push);
    const dismiss = useToastStore((state) => state.dismiss);

    return {
        showToast: push,
        dismissToast: dismiss,
        success: (message: string, title?: string) => push({ tone: 'success', title, message }),
        error: (message: string, title?: string) => push({ tone: 'error', title, message }),
        info: (message: string, title?: string) => push({ tone: 'info', title, message }),
    };
}
